import { ExtendedAsanaTask, TaskFilters, SortConfig, GroupData, DashboardMetrics, TaskAge, AgeCategory } from '../types';

// OP Decision order for custom sorting
const OP_DECISION_ORDER: Record<string, number> = {
  'New': 1,
  'Review': 2,
  'P1': 3,
  'P2': 4,
  'P3': 5,
  'P4': 6,
  'TBD': 7
};

// Age indicators configuration
export const AGE_INDICATORS = {
  new: { dot: '🟢', label: 'New', title: (days: number) => `${days} days old - New request` },
  attention: { dot: '🟡', label: 'Needs Attention', title: (days: number) => `${days} days old - Needs attention` },
  urgent: { dot: '🔴', label: 'Urgent', title: (days: number) => `${days} days old - Urgent review needed` },
  overdue: { dot: '⚫', label: 'Overdue', title: (days: number) => `${days} days old - Overdue for review` }
};

// Cache Intl formatters at module level
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});

const numberFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

// Get the first word of a decision for sorting (FIXED: now pure function)
export const getDecisionFirstWord = (decision?: string): string => {
  if (!decision) return 'TBD';
  return decision.split(' ')[0] || 'TBD';
};

// Get P1/P2 decisions dynamically (FIXED: now pure function taking tasks as argument)
export const getP1P2Decisions = (tasks: ExtendedAsanaTask[]): string[] => {
  const allDecisions = Array.from(new Set(tasks.map(task => task.op_decision))).filter(Boolean) as string[];
  return allDecisions.filter(decision => {
    const firstWord = getDecisionFirstWord(decision);
    return firstWord === 'P1' || firstWord === 'P2';
  });
};

// Safely parse numeric values
export const safeParseFloat = (value: unknown): number => {
  if (value === null || value === undefined || value === '') return 0;
  
  if (typeof value === 'number') return isNaN(value) ? 0 : value;
  
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  return 0;
};

// Format currency in compact form
export const formatCompactCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};

// Format currency
export const formatCurrency = (value: number): string => {
  return currencyFormatter.format(value);
};

// Format number with decimals
export const formatNumber = (value: number): string => {
  return numberFormatter.format(value);
};

// Format date
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Format date and time with details
export const formatDateTimeDetailed = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

// Calculate task age
export const getTaskAge = (createdAt: string): TaskAge => {
  const daysDiff = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysDiff <= 7) return { days: daysDiff, category: 'new' };
  if (daysDiff <= 14) return { days: daysDiff, category: 'attention' };
  if (daysDiff <= 30) return { days: daysDiff, category: 'urgent' };
  return { days: daysDiff, category: 'overdue' };
};

// Check if task is a new request
export const isNewRequest = (task: ExtendedAsanaTask): boolean => {
  const opDecision = task.op_decision;
  return !opDecision || 
         opDecision === 'New' || 
         opDecision === 'Review in Progress' ||
         opDecision === 'Under Review' ||
         opDecision.trim() === '';
};

// Apply filters to tasks
export const applyFilters = (
  tasks: ExtendedAsanaTask[], 
  filters: TaskFilters, 
  activeTab: 'all' | 'new'
): ExtendedAsanaTask[] => {
  return tasks.filter(task => {
    // Apply auto-filtering for "New Request" tab
    if (activeTab === 'new' && !isNewRequest(task)) {
      return false;
    }

    // Handle OP Decision filtering with support for empty/unset values
    const taskOpDecision = task.op_decision;
    const opDecisionMatch = filters.op_decision.length === 0 || 
      filters.op_decision.includes(taskOpDecision || '') ||
      (filters.op_decision.includes('Unset') && (!taskOpDecision || taskOpDecision.trim() === ''));

    // Handle Mission Fit filtering
    const taskMissionFit = task.mission_fit;
    const missionFitMatch = filters.mission_fit.length === 0 || 
      filters.mission_fit.includes(taskMissionFit || '') ||
      (filters.mission_fit.includes('Unset') && (!taskMissionFit || taskMissionFit.trim() === ''));

    // Handle Target Year filtering
    const taskTargetYear = task.target_year;
    const targetYearMatch = filters.target_year.length === 0 || 
      filters.target_year.includes(taskTargetYear || '') ||
      (filters.target_year.includes('Unset') && (!taskTargetYear || taskTargetYear.trim() === ''));

    // Handle Request Type filtering
    const taskRequestType = task.request_type;
    const requestTypeMatch = filters.request_type.length === 0 || 
      filters.request_type.includes(taskRequestType || '') ||
      (filters.request_type.includes('Unset') && (!taskRequestType || taskRequestType.trim() === ''));

    // Handle Status filtering
    const taskStatus = task.status;
    const statusMatch = filters.status.length === 0 || 
      filters.status.includes(taskStatus || '') ||
      (filters.status.includes('Unset') && (!taskStatus || taskStatus.trim() === ''));

    return (
      (filters.name === '' || task.name.toLowerCase().includes(filters.name.toLowerCase())) &&
      (filters.program.length === 0 || filters.program.includes(task.program)) &&
      (filters.theme.length === 0 || filters.theme.includes(task.theme)) &&
      (filters.teamAssigned.length === 0 || filters.teamAssigned.includes(task.assignee?.name || task.teamAssigned || '')) &&
      (filters.l7_sdm.length === 0 || filters.l7_sdm.includes(task.l7_sdm || task.l7_manager || '')) &&
      opDecisionMatch &&
      missionFitMatch &&
      targetYearMatch &&
      requestTypeMatch &&
      statusMatch
    );
  });
};

// Sort tasks based on sort configuration
export const sortTasks = (tasks: ExtendedAsanaTask[], sortConfig: SortConfig): ExtendedAsanaTask[] => {
  if (!sortConfig.key || !sortConfig.direction) {
    return tasks;
  }

  return [...tasks].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortConfig.key) {
      case 'op_decision':
        const aDecisionFirstWord = getDecisionFirstWord(a.op_decision);
        const bDecisionFirstWord = getDecisionFirstWord(b.op_decision);
        aValue = OP_DECISION_ORDER[aDecisionFirstWord] || 999;
        bValue = OP_DECISION_ORDER[bDecisionFirstWord] || 999;
        break;
      case 'annualized_entitlement':
        aValue = parseFloat(a.annualized_entitlement as string) || 0;
        bValue = parseFloat(b.annualized_entitlement as string) || 0;
        break;
      case 'program':
        aValue = a.program || '';
        bValue = b.program || '';
        break;
      case 'teamAssigned':
        aValue = a.assignee?.name || a.teamAssigned || '';
        bValue = b.assignee?.name || b.teamAssigned || '';
        break;
      case 'l7_manager':
        aValue = a.l7_manager || '';
        bValue = b.l7_manager || '';
        break;
      case 'created_at':
        aValue = new Date(a.created_at).getTime();
        bValue = new Date(b.created_at).getTime();
        break;
      default:
        aValue = (a as any)[sortConfig.key] || '';
        bValue = (b as any)[sortConfig.key] || '';
    }

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    let comparison = 0;
    if (aValue > bValue) {
      comparison = 1;
    } else if (aValue < bValue) {
      comparison = -1;
    }

    return sortConfig.direction === 'desc' ? comparison * -1 : comparison;
  });
};

// Calculate next sort direction
export const nextSortDirection = (
  currentKey: string,
  clickedKey: string,
  currentDirection: 'asc' | 'desc' | null
): SortConfig => {
  let direction: 'asc' | 'desc' | null = 'asc';
  
  if (currentKey === clickedKey) {
    if (currentDirection === 'asc') {
      direction = 'desc';
    } else if (currentDirection === 'desc') {
      direction = null;
    }
  }
  
  return { key: clickedKey, direction };
};

// Group tasks by field (FIXED: duplicate fallback bug)
export const groupTasks = (tasks: ExtendedAsanaTask[], groupBy: string): GroupData[] | null => {
  if (groupBy === 'none') return null;
  
  const groups = new Map<string, GroupData>();
  
  tasks.forEach(task => {
    let groupKey: string;
    
    switch (groupBy) {
      case 'op_decision':
        groupKey = task.op_decision || 'Unknown';
        break;
      case 'program':
        groupKey = task.program || 'Unknown';
        break;
      case 'l7_manager':
        groupKey = task.l7_sdm || task.l7_manager || 'Unknown';
        break;
      case 'impact_metrics':
        // FIXED: Was (task as any).impact_metrics || (task as any).impact_metrics || 'Unknown'
        groupKey = task.impact_metrics || 'Unknown';
        break;
      default:
        groupKey = 'Unknown';
    }
    
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        name: groupKey,
        tasks: [],
        totalEntitlement: 0,
        totalSDE: 0,
        taskCount: 0
      });
    }
    
    const group = groups.get(groupKey)!;
    group.tasks.push(task);
    group.totalEntitlement += parseFloat(task.annualized_entitlement as string) || 0;
    group.totalSDE += parseFloat(task.hc_sde as any) || 0;
    group.taskCount++;
  });
  
  return Array.from(groups.values()).sort((a, b) => 
    b.totalEntitlement - a.totalEntitlement
  );
};

// Compute dashboard metrics
export const computeDashboardMetrics = (
  filteredTasks: ExtendedAsanaTask[],
  l7Data: any,
  filters: TaskFilters
): DashboardMetrics => {
  return {
    totalEntitlement: filteredTasks.reduce((sum, task) => {
      const entitlement = safeParseFloat(task.annualized_entitlement);
      return sum + entitlement;
    }, 0),
    totalHcSde: filteredTasks
      .filter(t => {
        const opDecision = t.op_decision;
        const firstWord = getDecisionFirstWord(opDecision);
        return firstWord !== 'P3' && firstWord !== 'P4';
      })
      .reduce((sum, task) => {
        const hcSde = parseFloat(task.hc_sde as any) || 0;
        return sum + hcSde;
      }, 0),
    totalTasks: filteredTasks.length,
    p1OpPriority: filteredTasks
      .filter(t => {
        const opDecision = t.op_decision;
        const firstWord = getDecisionFirstWord(opDecision);
        return firstWord === 'P1';
      })
      .reduce((sum, task) => {
        const hcSde = parseFloat(task.hc_sde as any) || 0;
        return sum + hcSde;
      }, 0),
    p2OpPriority: filteredTasks
      .filter(t => {
        const opDecision = t.op_decision;
        const firstWord = getDecisionFirstWord(opDecision);
        return firstWord === 'P2';
      })
      .reduce((sum, task) => {
        const hcSde = parseFloat(task.hc_sde as any) || 0;
        return sum + hcSde;
      }, 0),
    BTLOpPriority: filteredTasks
      .filter(t => {
        const opDecision = t.op_decision;
        const firstWord = getDecisionFirstWord(opDecision);
        return firstWord === 'P3' || firstWord === 'P4';
      })
      .reduce((sum, task) => {
        const hcSde = parseFloat(task.hc_sde as any) || 0;
        return sum + hcSde;
      }, 0),
    tbdTasks: filteredTasks.filter(t => {
      const opDecision = t.op_decision;
      return !opDecision || opDecision.trim() === '' || opDecision === 'TBD';
    }).length,
    l7Coverage: l7Data.coveragePercentage,
    l7MatchedCount: l7Data.matchedCount,
    l7TotalCount: l7Data.totalCount,
    targetSDE: l7Data.totalTargetSDE,
    actualSDE: l7Data.totalActualSDE,
    targetHC: l7Data.totalTargetHC,
    actualHC: l7Data.totalActualHC,
    rtbHC: l7Data.totalRTB
  };
};

// Get unique values for a field
export const uniqueValues = (tasks: ExtendedAsanaTask[], field: keyof ExtendedAsanaTask): string[] => {
  return Array.from(new Set(tasks.map(task => String(task[field])))).filter(Boolean).sort();
};

// Get unique assignees
export const uniqueAssignees = (tasks: ExtendedAsanaTask[]): string[] => {
  return Array.from(new Set(tasks.map(task => task.assignee?.name || task.teamAssigned || ''))).filter(Boolean).sort();
};

// Create default filters
export const createDefaultFilters = (): TaskFilters => {
  const currentYear = new Date().getFullYear().toString();
  
  return {
    name: '',
    program: [],
    theme: [],
    teamAssigned: [],
    l7_sdm: [],
    op_decision: [],
    mission_fit: [],
    target_year: [currentYear],
    request_type: [],
    status: []
  };
};

// NEW: Quarter calculation from a date
export const getQuarterFromDate = (date: Date): string => {
  const m = date.getMonth();
  if (m < 3) return 'Q1';
  if (m < 6) return 'Q2';
  if (m < 9) return 'Q3';
  return 'Q4';
};

// NEW: Human-readable quarter label from a date string
export const getQuarterLabel = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');  // Avoid timezone shift
  if (isNaN(d.getTime())) return '—';
  return `${getQuarterFromDate(d)} ${d.getFullYear()}`;
};

// NEW: NOW/NEXT/LATER bucketing from a date string
export const getTimeHorizon = (dateStr: string | null | undefined): 'NOW' | 'NEXT' | 'LATER' | null => {
  if (!dateStr) return null;
  const launch = new Date(dateStr + 'T00:00:00');
  if (isNaN(launch.getTime())) return null;
  const now = new Date();
  const cq = getQuarterFromDate(now);
  const lq = getQuarterFromDate(launch);
  if (launch.getFullYear() === now.getFullYear() && lq === cq) return 'NOW';
  const diff = (launch.getFullYear() - now.getFullYear()) * 12 + (launch.getMonth() - now.getMonth());
  if (diff > 0 && diff <= 6) return 'NEXT';
  return 'LATER';
};

// NEW: Status color mapping (all 10 Asana statuses)
export const STATUS_COLORS: Record<string, { color: string; bg: string }> = {
  'Not Started':  { color: '#8E8E93', bg: '#F2F2F7' },
  'Scheduled':    { color: '#6D9F3B', bg: '#E8F5E0' },
  'In Progress':  { color: '#007AFF', bg: '#E5F1FF' },
  'In Review':    { color: '#AF8F00', bg: '#FFF8DC' },
  'Blocked':      { color: '#FF3B30', bg: '#FFE5E5' },
  'At Risk':      { color: '#FF9500', bg: '#FFF3E0' },
  'Delayed':      { color: '#E8720C', bg: '#FFF0E0' },
  'On Hold':      { color: '#AF52DE', bg: '#F5E6FF' },
  'Completed':    { color: '#34C759', bg: '#E0F8E6' },
  'Cancelled':    { color: '#5856D6', bg: '#EDECFF' },
};

export const getStatusColor = (status?: string): { color: string; bg: string } =>
  STATUS_COLORS[status || ''] || { color: '#8E8E93', bg: '#F2F2F7' };
